import React from 'react'
import { FlightRouteMap, CoverageMap, useMapImage, routeLegend, coverageLegend } from './ReportMaps'
import {
    COLORS, RISK_COLORS, fmtNum, fmtPct, fmtAcres, fmtDate, fmtValue, fmtDuration, shortLocation, tint,
    gradeColor, factorColor, isNum, fmtArea, fmtPctShort, getSummary, getYieldImpact, getSuggestions,
} from './sprayReportUtils'
import pdrlLogo from '../../pdrl_logo.png'
const PDF_PAGES = 3
const pageNo = (n) => String(n).padStart(2, '0')

const tinted = (color) => ({ color, background: tint(color, 0.08), borderColor: tint(color, 0.35) })

const Field = ({ label, value, wide }) => (
    <div className={`sqr-field ${wide ? 'sqr-field-wide' : ''}`}>
        <div className="sqr-label">{label}</div>
        <div className="sqr-value" title={typeof value === 'string' ? value : undefined}>{value}</div>
    </div>
)

const Chip = ({ label, value }) => (
    <span className="sqr-chip"><span className="sqr-chip-label">{label}</span>{value}</span>
)

const Legend = ({ items, grid }) => (
    <div className={`sqr-legend ${grid ? 'sqr-legend-grid' : ''}`}>
        {items.map(item => (
            <span key={item.label}>
                {item.type === 'start-end'
                    ? <><i className="sqr-swatch-start" /><i className="sqr-swatch-end" /></>
                    : <i className={`sqr-swatch sqr-swatch-${item.type}`} style={{ background: item.type === 'box' ? item.color : undefined, borderColor: item.color }} />}
                {item.label}
            </span>
        ))}
    </div>
)

const ReportHeader = ({ report }) => {
    const f = report.flight_summary || {}
    const score = report.spraying_efficiency_score || {}
    const color = gradeColor(score)
    const sprayWidth = report.analysis_parameters?.spray_width_m
    return (
        <div className="sqr-header">
            <img className="sqr-header-logo" alt="PDRL" src={pdrlLogo} />
            <div className="sqr-header-inner">
                <div className="sqr-header-title">
                    {(score.name || 'Drone Spraying Quality Score').toUpperCase()}
                </div>
                <div className="sqr-header-main">
                    <div>
                        <div className="sqr-header-plan">{`${f.project_name || 'NA'} / ${f.plan_name || 'NA'}`}</div>
                        <div className="sqr-header-id">{f.flight_id}</div>
                    </div>
                    <div className="sqr-header-score">
                        <div className="sqr-header-score-value">
                            <div className="sqr-label">Quality Score</div>
                            <div className="sqr-header-score-number" style={{ color }}>
                                {fmtValue(score.score, '%')} <span className="sqr-muted">/ {score.max_score || 100}</span>
                            </div>
                        </div>
                        <div className="sqr-grade-box" style={tinted(color)}>
                            <div className="sqr-label">Grade</div>
                            <div className="sqr-grade-letter">{score.grade || 'NA'}</div>
                            <div className="sqr-grade-verdict">{score.verdict}</div>
                        </div>
                    </div>
                </div>
                <div className="sqr-chips">
                    <Chip label="Date" value={fmtDate(f.start_time)} />
                    <Chip label="Location" value={shortLocation(f.location)} />
                    <Chip label="Pilot" value={f.pilot_name || 'NA'} />
                    <Chip label="Duration" value={f.duration_text || fmtDuration(f.duration_seconds)} />
                    <Chip label="Distance" value={isNum(f.distance_flown_m) ? `${fmtNum(f.distance_flown_m)} m` : 'NA'} />
                    <Chip label="Spray Width" value={isNum(sprayWidth) ? `${sprayWidth} m` : 'NA'} />
                </div>
            </div>
        </div>
    )
}

const FlightDetailsSection = ({ report }) => {
    const f = report.flight_summary || {}
    const sprayWidth = report.analysis_parameters?.spray_width_m
    return (
        <div className="sqr-card">
            <h3 className="sqr-section-title">Flight Details</h3>
            <div className="sqr-fields">
                <Field label="Date" value={fmtDate(f.start_time)} />
                <Field label="Start Time" value={fmtDate(f.start_time, 'hh:MM:ss TT')} />
                <Field label="End Time" value={fmtDate(f.end_time, 'hh:MM:ss TT')} />
                <Field label="Duration" value={f.duration_text || fmtDuration(f.duration_seconds)} />
                <Field label="Pilot Name" value={f.pilot_name || 'NA'} />
                <Field label="Spraying Time" value={fmtDuration(f.spraying_time_seconds)} />
                <Field label="Avg Altitude" value={isNum(f.avg_spray_altitude_m) ? `${Number(f.avg_spray_altitude_m).toFixed(2)} m` : 'NA'} />
                <Field label="Avg Speed" value={isNum(f.avg_spray_speed_mps) ? `${Number(f.avg_spray_speed_mps).toFixed(2)} m/s` : 'NA'} />
                <Field label="Distance Flown" value={isNum(f.distance_flown_m) ? `${fmtNum(f.distance_flown_m)} m` : 'NA'} />
            </div>
            <div className="sqr-fields sqr-fields-divided">
                <Field label="Location" value={f.location || 'NA'} wide />
            </div>
            <div className="sqr-fields sqr-fields-2 sqr-fields-divided">
                <Field label="Drone Name" value={f.drone_name || 'NA'} />
                <div className="sqr-field">
                    <div className="sqr-label">Drone ID</div>
                    <div className="sqr-value sqr-break">{f.drone_id || 'NA'}</div>
                </div>
                <Field label="Drone UIN" value={f.drone_uin || 'NA'} />
                <Field label="Drone Model" value={f.drone_model || 'NA'} />
                <Field label="Firmware Version" value={f.firmware_version || 'NA'} />
                <Field label="GCS Build" value={f.gcs_build || 'NA'} />
                <Field label="Spray Width" value={isNum(sprayWidth) ? `${sprayWidth} m` : 'NA'} />
            </div>
        </div>
    )
}

const FlightRouteSection = ({ report, paths }) => {
    const center = report.flight_summary?.center
    const image = useMapImage(report.flight_route_map?.image_url)
    return (
        <div className="sqr-card sqr-card-flush">
            <h3 className="sqr-section-title">Flight Route</h3>
            <FlightRouteMap report={report} paths={paths} image={image} />
            <div className="sqr-map-footer">
                <Legend items={routeLegend(!!image.url)} />
                {center && isNum(center.lat) && isNum(center.lon) &&
                    <div className="sqr-note-small">Map centre: {Number(center.lat).toFixed(5)}° N, {Number(center.lon).toFixed(5)}° E</div>}
            </div>
        </div>
    )
}

const ScoreRing = ({ score, max, color }) => {
    const r = 54, c = 2 * Math.PI * r
    const ratio = isNum(score) ? Math.min(Math.max(score / (max || 100), 0), 1) : 0
    return (
        <svg className="sqr-ring" viewBox="0 0 130 130">
            <circle cx="65" cy="65" r={r} fill="none" stroke="#EEF0F4" strokeWidth="10" />
            <circle cx="65" cy="65" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={`${c * ratio} ${c}`} transform="rotate(-90 65 65)" />
            <text x="65" y="68" textAnchor="middle" fontSize="29" fontWeight="700" fill="#1F2937">{fmtValue(score, '%')}</text>
            <text x="65" y="86" textAnchor="middle" fontSize="12" fill="#9CA3AF">out of {max || 100}</text>
        </svg>
    )
}

const StackedBar = ({ coverage }) => {
    const parts = [
        { pct: coverage.covered_once?.pct_of_target, color: COLORS.coveredOnce },
        { pct: coverage.overlapped_total?.pct_of_target, color: COLORS.overlap2x },
        { pct: coverage.missed?.pct_of_target, color: COLORS.missed },
    ].filter(p => isNum(p.pct) && p.pct > 0)
    if (!parts.length) return null
    return <>
        <div className="sqr-stack">
            {parts.map((p, i) => <div key={i} style={{ width: `${p.pct}%`, background: p.color }} />)}
        </div>
        <div className="sqr-stack-labels">
            {parts.filter(p => p.pct >= 8).map((p, i) => <span key={i} style={{ color: p.color }}>{Math.round(p.pct)}%</span>)}
        </div>
    </>
}

const FactorRow = ({ factor }) => {
    const earned = Number(factor.points_earned) || 0
    const color = factorColor(factor)
    return (
        <div className="sqr-factor" title={factor.description}>
            <span>{factor.name}</span>
            <div className="sqr-factor-track">
                <div style={{ width: `${Math.max(0, Math.min(100, (earned / (Number(factor.points_possible) || 1)) * 100))}%`, background: color }} />
            </div>
            <b>
                <span style={{ color: earned > 0 ? color : '#9CA3AF' }}>{fmtNum(factor.points_earned, 1)}</span>
                <span className="sqr-muted">/{fmtNum(factor.points_possible, 0)}</span>
            </b>
        </div>
    )
}

const SprayCoverageSection = ({ report }) => {
    const score = report.spraying_efficiency_score || {}
    const cov = report.coverage || {}
    const workDone = cov.work_done
    const allowance = workDone?.unsprayable_allowance_pct ?? report.analysis_parameters?.unsprayable_allowance_pct
    const color = gradeColor(score)
    const factors = (score.factors || []).filter(f => f && typeof f === 'object')
    const areaWithM2 = (m2) => <>{fmtAcres(m2)} <span className="sqr-muted-sm">{fmtNum(m2)} m²</span></>
    return (
        <div className="sqr-card">
            <h3 className="sqr-section-title">Spray Coverage</h3>
            <div className="sqr-score">
                <ScoreRing score={score.score} max={score.max_score} color={color} />
                <div>
                    <div className="sqr-label">{score.name}</div>
                    <div className="sqr-verdict" style={{ color }}>{score.verdict || 'NA'}</div>
                    <span className="sqr-badge" style={tinted(color)}>GRADE {score.grade || 'NA'}</span>
                </div>
            </div>
            <div className="sqr-fields">
                <Field label="Target Area" value={areaWithM2(cov.target_area_m2)} />
                <Field label="Sprayed Area" value={areaWithM2(cov.sprayed_at_least_once?.area_m2)} />
                {workDone && <Field label="Work Done" value={fmtPct(workDone.pct)} />}
                <Field label="Sprayed At Least Once" value={fmtPct(cov.sprayed_at_least_once?.pct_of_target)} />
                <Field label="Covered Once" value={fmtPct(cov.covered_once?.pct_of_target)} />
                <Field label="Overlap (2x+)" value={fmtPct(cov.overlapped_total?.pct_of_target)} />
                <Field label="Missed" value={fmtPct(cov.missed?.pct_of_target)} />
                {workDone && <Field label="Obstacle Allowance" value={fmtPctShort(allowance)} />}
                {workDone && <Field label="Missed Beyond Allowance" value={fmtPct(workDone.missed_beyond_allowance?.pct_of_target)} />}
                <Field label="Outside Target" value={isNum(cov.off_target_spray?.area_m2) ? `${fmtNum(cov.off_target_spray.area_m2)} m²` : 'NA'} />
                <Field label="Uniformity" value={isNum(cov.uniformity_index) ? Number(cov.uniformity_index).toFixed(2) : 'NA'} />
                <Field label="Unsprayed Patches" value={fmtValue(cov.missed_detail?.patch_count)} />
            </div>
            <StackedBar coverage={cov} />
            {!!factors.length && <>
                <div className="sqr-factor-head"><h4 className="sqr-subtitle">Score Breakdown</h4></div>
                <div className="sqr-factors" style={{ borderTop: 'none' }}>
                    {factors.map(f => <FactorRow key={f.key || f.name} factor={f} />)}
                </div>
            </>}
        </div>
    )
}

const coverageNote = (report, paths, fromImage) => {
    const params = report.analysis_parameters || {}
    if (fromImage) return `Each ${params.grid_resolution_m || 0.5} m cell inside the blue target boundary is coloured by how many times it was sprayed; red was never sprayed. The drone's path is drawn in black with arrows showing the direction of travel; the green dot marks the start and the red square the end.`
    if (paths.length) return `Green is the ${fmtValue(params.spray_width_m, ' m')} spray swath along the drone path inside the blue target boundary; red was never sprayed. The drone's path is drawn as a dashed line, with arrows showing the direction of travel.`
    return 'Blue is the target spray boundary and grey the field outline. The coverage figures on the left are measured on a 0.5 m grid.'
}

const CoverageMapSection = ({ report, paths }) => {
    const image = useMapImage(report.coverage_map?.image_url)
    return (
        <div className="sqr-card sqr-card-flush">
            <h3 className="sqr-section-title">Coverage Map</h3>
            <CoverageMap report={report} paths={paths} image={image} />
            <div className="sqr-map-footer">
                <Legend items={coverageLegend(!!image.url)} grid />
                <div className="sqr-note-small">{coverageNote(report, paths, !!image.url)}</div>
            </div>
        </div>
    )
}

const SummarySection = ({ report }) => {
    const summary = getSummary(report)
    return (
        <div className="sqr-card">
            <h3 className="sqr-section-title">Summary</h3>
            <div className="sqr-paragraphs">
                {(summary.paragraphs || []).map((p, i) => <p key={i} className="sqr-text">{p}</p>)}
                {summary.key_finding && <p className="sqr-key-finding">{summary.key_finding}</p>}
            </div>
            {(summary.notes || []).map((n, i) => <div key={i} className="sqr-callout"><b>{n.title}</b> {n.text}</div>)}
        </div>
    )
}

const YieldImpactSection = ({ report }) => {
    const impact = getYieldImpact(report)
    const cov = report.coverage || {}
    const riskColor = RISK_COLORS[impact.risk_level] || RISK_COLORS.unknown
    const stats = [
        { label: 'Area treated', s: cov.sprayed_at_least_once },
        { label: 'Area left untreated', s: cov.missed },
        { label: 'Area double-dosed', s: cov.overlapped_total },
    ]
    return (
        <div className="sqr-card">
            <h3 className="sqr-section-title">Impact on Yield</h3>
            <div className="sqr-impact-head">
                <span className="sqr-pill" style={tinted(riskColor)}>{impact.risk_label}</span>
                <b>{impact.headline}</b>
            </div>
            <div className="sqr-impact-stats">
                {stats.map(({ label, s }) => (
                    <div key={label}>
                        <div className="sqr-label">{label}</div>
                        <div className="sqr-value">{fmtPct(s?.pct_of_target, 0)} <span className="sqr-muted-sm">{fmtArea(s?.area_m2)}</span></div>
                    </div>
                ))}
                <div>
                    <div className="sqr-label">Spray outside target</div>
                    <div className="sqr-value">{fmtPct(cov.off_target_spray?.pct_of_total_spray)} <span className="sqr-muted-sm">of spray</span></div>
                </div>
            </div>
            {(impact.items || []).map((item, i) => (
                <div key={i} className="sqr-list-item sqr-bullet">
                    <b>{item.title}</b>
                    <p className="sqr-text">{item.text}</p>
                </div>
            ))}
        </div>
    )
}

const SuggestionsSection = ({ report }) => {
    const suggestions = getSuggestions(report)
    if (!suggestions.length) return null
    return (
        <div className="sqr-card">
            <h3 className="sqr-section-title">Improvement Suggestions</h3>
            <div className="sqr-suggestions">
                {suggestions.map((s, i) => (
                    <div key={i} className="sqr-list-item sqr-suggestion">
                        <span className="sqr-step">{i + 1}</span>
                        <div>
                            <b>{s.title}</b>
                            <p className="sqr-text">{s.text}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const ReportBody = ({ report, paths }) => (
    <div className="sqr-report">
        <ReportHeader report={report} />
        <div className="sqr-body">
            <div className="sqr-row">
                <FlightDetailsSection report={report} />
                <FlightRouteSection report={report} paths={paths} />
            </div>
            <div className="sqr-row">
                <SprayCoverageSection report={report} />
                <CoverageMapSection report={report} paths={paths} />
            </div>
            <SummarySection report={report} />
            <YieldImpactSection report={report} />
            <SuggestionsSection report={report} />
            <div className="sqr-footer">
                <span>Powered By - Passenger Drone Research Private Limited</span>
                <span>Page No.- {pageNo(PDF_PAGES)}/{pageNo(PDF_PAGES)}</span>
            </div>
        </div>
    </div>
)

export default ReportBody
